import React from 'react';
import type { TopPerformer } from '@troia-v3/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui';

import { DashCard, formatBRLShort, formatPct } from '@/shared/components/ui';

interface TopPerformersSpotlightProps {
  performers: TopPerformer[];
}

export const TopPerformersSpotlight: React.FC<TopPerformersSpotlightProps> = ({ performers }) => {
  return (
    <DashCard
      icon="award"
      title="Top 5 vendedores do mês"
      subtitle="Ranking por receita ganha"
      testId="top-performers-spotlight"
    >
      <div className="grid grid-cols-1 min-[521px]:grid-cols-2 min-[900px]:grid-cols-3 min-[1301px]:grid-cols-5 gap-3">
        {performers.length === 0 && (
          <div
            className="col-span-full text-center text-xs py-6"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Nenhum vendedor com receita no período.
          </div>
        )}
        {performers.map((p, idx) => {
          const isFirst = idx === 0;
          const goalPct = p.goalPct ?? 0;
          const hasGoalAchieved = p.goalPct !== null && goalPct >= 50;
          return (
            <div
              key={p.userId}
              className="rounded-xl p-3 flex flex-col gap-2 min-w-0"
              style={{
                backgroundColor: isFirst ? 'rgba(139, 92, 246, 0.1)' : 'var(--theme-card-background)',
                border: `1px solid ${isFirst ? 'rgba(139, 92, 246, 0.25)' : 'var(--theme-card-border)'}`,
              }}
              data-testid={`top-performer-${idx}`}
            >
              <div className="flex items-center justify-between">
                <span
                  className="text-xs font-bold"
                  style={{ color: isFirst ? 'var(--theme-primary, #8b5cf6)' : 'var(--theme-muted-text)' }}
                >
                  #{idx + 1}
                </span>
                <Avatar className="w-8 h-8" isSquircle>
                  {p.avatarUrl && <AvatarImage src={p.avatarUrl} alt={p.name} isSquircle />}
                  <AvatarFallback isSquircle>{p.initials}</AvatarFallback>
                </Avatar>
              </div>
              <div className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
                {p.name}
              </div>
              <div
                className="text-lg font-bold leading-none"
                style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
              >
                {formatBRLShort(p.revenue)}
              </div>
              <div className="text-[11px]" style={{ color: 'var(--theme-muted-text)' }}>
                {p.deals} deals · conv. {formatPct(p.conversionRate)}
              </div>
              {p.goalPct !== null ? (
                <>
                  <div
                    className="h-1.5 rounded-full overflow-hidden mt-0.5"
                    style={{ backgroundColor: 'var(--theme-card-border)' }}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min(100, goalPct)}%`,
                        backgroundColor: 'var(--theme-primary, #8b5cf6)',
                      }}
                    />
                  </div>
                  <div
                    className="text-[11px] font-semibold"
                    style={{
                      color: hasGoalAchieved
                        ? 'var(--theme-success-color, #10b981)'
                        : 'var(--theme-muted-text)',
                    }}
                  >
                    {Math.round(goalPct)}% da meta
                  </div>
                </>
              ) : (
                <div className="text-[11px] font-semibold" style={{ color: 'var(--theme-muted-text)' }}>
                  Sem meta
                </div>
              )}
            </div>
          );
        })}
      </div>
    </DashCard>
  );
};

TopPerformersSpotlight.displayName = 'TopPerformersSpotlight';
